import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import './process-create.css'
import { Routes } from '../../../routes'
import { FamlaServices } from '../../../services'
import { createCollectDataCollection } from '../../../services/api/collectDataService'
import { useAppSelector } from '../../../stores'
import { getBrandingColor, getBrandingColorHover } from '../../../utils/brandingStyles'

interface ApiOption {
    _id: string
    name: string
    description?: string
};

const DataCollectCreate = () => {
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const navigate = useNavigate()
    const language = localStorage.getItem('currentLanguage') === "fr" ? "french" : "english"

    const initialCollectLocale = (): 'fr' | 'en' => {
        try {
            const v = localStorage.getItem('currentLanguage');
            if (v === 'fr') return 'fr';
        } catch {
            /* ignore */
        }
        return 'en';
    };

    const [collectLocale, setCollectLocale] = React.useState<'fr' | 'en'>(initialCollectLocale);

    const [newCollection, setNewCollection] = React.useState({
        name: '',
        industry: '',
        function: '',
        isSubmit: false,
    });

    const [industry, setIndustry] = React.useState<ApiOption[]>([])
    const [functions, setFunctions] = React.useState<ApiOption[]>([
        { _id: '1', name: t('form.dataCollect.functions.finance') },
        { _id: '2', name: t('form.dataCollect.functions.hr') },
        { _id: '3', name: t('form.dataCollect.functions.operations') },
        { _id: '4', name: t('form.dataCollect.functions.it') },
        { _id: '5', name: t('form.dataCollect.functions.sales') },
        { _id: '6', name: t('form.dataCollect.functions.marketing') },
        { _id: '7', name: t('form.dataCollect.functions.legal') },
        { _id: '8', name: t('form.dataCollect.functions.other') }
    ])

    const sortedIndustry = industry
        .slice()
        .sort((a, b) => {
            const isAOther = ['autre', 'other'].includes(a.name.toLowerCase());
            const isBOther = ['autre', 'other'].includes(b.name.toLowerCase());

            if (isAOther && !isBOther) return 1;
            if (!isAOther && isBOther) return -1;
            return a.name.localeCompare(b.name);
        });

    const sortedFunctions = functions
        .slice()
        .sort((a, b) => {
            const isAOther = ['autre', 'other'].includes(a.name.toLowerCase());
            const isBOther = ['autre', 'other'].includes(b.name.toLowerCase());

            if (isAOther && !isBOther) return 1;
            if (!isAOther && isBOther) return -1;
            return a.name.localeCompare(b.name);
        });

    const selectIndustryOptions = sortedIndustry.map((ind) => ({
        label: ind.name,
        value: ind.name,
    }));

    const selectFunctionOptions = sortedFunctions.map((f) => ({
        label: f.name,
        value: f.name,
    }));

    const [alert, setAlert] = React.useState({
        isActive: false,
        type: '',
        message: ''
    })

    React.useEffect(() => {
        setFunctions([
            { _id: '1', name: t('form.dataCollect.functions.finance') },
            { _id: '2', name: t('form.dataCollect.functions.hr') },
            { _id: '3', name: t('form.dataCollect.functions.operations') },
            { _id: '4', name: t('form.dataCollect.functions.it') },
            { _id: '5', name: t('form.dataCollect.functions.sales') },
            { _id: '6', name: t('form.dataCollect.functions.marketing') },
            { _id: '7', name: t('form.dataCollect.functions.legal') },
            { _id: '8', name: t('form.dataCollect.functions.other') }
        ]);

        const fetchDropdowns = async () => {
            try {
                let indData = await FamlaServices("api_host", `/industry/?lang=${language}`, "GET", "", users.access_token);
                if (indData.status === 200) {
                    setIndustry(indData.body.data)
                }

            } catch (error) {
                console.error("Error fetching form options:", error);
            }
        }
        fetchDropdowns()
    }, [users.access_token, language, t])

    const handleChange = (event: any) => {
        const { name, value } = event.target;

        if (name === 'name') {
            setNewCollection({ ...newCollection, name: value })
        } else if (name === 'industry') {
            setNewCollection({ ...newCollection, industry: value })
        } else if (name === 'function') {
            setNewCollection({ ...newCollection, function: value })
        } else if (name === 'collectLocale') {
            if (value === 'fr' || value === 'en') setCollectLocale(value);
        }
    };

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();

        if (newCollection.name.trim().length === 0) {
            setAlert({ isActive: true, type: 'danger', message: t('form.nameRequired') });
            return;
        }

        if (!users.access_token) {
            setAlert({ isActive: true, type: 'danger', message: t('words.error') || 'Not authenticated' });
            return;
        }

        setNewCollection({ ...newCollection, isSubmit: true });

        try {
            const res = await createCollectDataCollection(
                {
                    name: newCollection.name.trim(),
                    locale: collectLocale,
                    ...(newCollection.industry ? { industry: newCollection.industry } : {}),
                    ...(newCollection.function ? { function: newCollection.function } : {}),
                },
                users.access_token
            );
            if (res.status === 201 && res.body?.data?.id) {
                navigate(`/process/data-collect/${res.body.data.id}`);
                return;
            }
            setAlert({
                isActive: true,
                type: 'danger',
                message: res.body?.description || t('words.error') || 'Request failed',
            });
        } catch (e) {
            setAlert({
                isActive: true,
                type: 'danger',
                message: e instanceof Error ? e.message : String(e),
            });
        } finally {
            setNewCollection((prev) => ({ ...prev, isSubmit: false }));
        }
    };

    const ORANGE = getBrandingColor();
    const ORANGE_DARK = getBrandingColorHover();
    const ORANGE_DIM = 'var(--pc-orange-dim)';

    const sInput: React.CSSProperties = {
        display: 'block',
        width: '100%',
        borderRadius: 10,
        padding: '12px 16px',
        fontSize: 18,
        outline: 'none',
        transition: 'border-color .15s',
    };
    const sLabel: React.CSSProperties = {
        display: 'block',
        fontSize: 15,
        fontWeight: 500,
        color: 'var(--pc-label)',
        marginBottom: 6,
        lineHeight: 1.4,
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        e.currentTarget.style.borderColor = ORANGE;
        e.currentTarget.style.boxShadow = `0 0 0 1px ${ORANGE}`;
    };
    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        e.currentTarget.style.removeProperty('border-color');
        e.currentTarget.style.removeProperty('box-shadow');
    };

    return (
        <div className="pc-wrap" style={{ minHeight: '100vh', background: 'var(--pc-page-bg)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 24px' }}>
            <div style={{ width: '100%', maxWidth: 640 }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <Link
                        to={Routes.HOMEVIEW}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 34, height: 34, borderRadius: 8,
                            background: 'var(--pc-surface)', border: '1px solid var(--pc-border)',
                            color: 'var(--pc-label)', textDecoration: 'none', flexShrink: 0,
                            transition: 'color .15s, border-color .15s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--pc-back-hover-text)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--pc-border-hover)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--pc-label)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--pc-border)'; }}
                        aria-label="Back"
                    >
                        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden>
                            <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Link>
                    <h1 style={{ margin: 0, fontSize: 20, fontWeight: 500, color: 'var(--pc-title)' }}>
                        {t('form.dataCollect.processOverview')}
                    </h1>
                </div>

                <p style={{ margin: '0 0 24px', fontSize: 15, lineHeight: 1.5, color: 'var(--pc-muted)' }}>
                    {t('form.dataCollect.subtitle')}
                </p>

                <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                    

                    <div>
                        <label style={sLabel} htmlFor="collect-name">
                            {t('form.dataCollect.collectionName')}
                            <span style={{ color: '#f87171', marginLeft: 2 }}>*</span>
                        </label>
                        <input
                            id="collect-name"
                            type="text"
                            name="name"
                            value={newCollection.name}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            placeholder={t('form.dataCollect.collectionNamePlaceholder')}
                            style={sInput}
                        />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={sLabel} htmlFor="collect-industry">{t('form.dataCollect.industryLabel')}</label>
                            <select
                                id="collect-industry"
                                name="industry"
                                value={newCollection.industry}
                                onChange={handleChange}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                style={{ ...sInput, appearance: 'none', cursor: 'pointer' }}
                            >
                                <option value="">{t('form.dataCollect.selectIndustry')}</option>
                                {selectIndustryOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={sLabel} htmlFor="collect-function">{t('form.dataCollect.functionLabel')}</label>
                            <select
                                id="collect-function"
                                name="function"
                                value={newCollection.function}
                                onChange={handleChange}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                style={{ ...sInput, appearance: 'none', cursor: 'pointer' }}
                            >
                                <option value="">{t('form.dataCollect.selectFunction')}</option>
                                {selectFunctionOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label style={sLabel} htmlFor="collect-locale">{t('form.dataCollect.collectionLanguageLabel')}</label>
                        <select
                            id="collect-locale"
                            name="collectLocale"
                            value={collectLocale}
                            onChange={handleChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            style={{ ...sInput, appearance: 'none', cursor: 'pointer' }}
                        >
                            <option value="fr">{t('form.dataCollect.collectionLanguageFr')}</option>
                            <option value="en">{t('form.dataCollect.collectionLanguageEn')}</option>
                        </select>
                        
                    </div>

                    {alert.isActive && (
                        <div
                            role="alert"
                            style={{
                                borderRadius: 8, padding: '12px 16px', fontSize: 15, fontWeight: 500,
                                background: alert.type === 'danger'
                                    ? 'var(--pc-alert-danger-bg)'
                                    : alert.type === 'warning'
                                      ? 'var(--pc-alert-warning-bg)'
                                      : ORANGE_DIM,
                                border: `1px solid ${
                                    alert.type === 'danger'   ? '#f87171'
                                    : alert.type === 'warning' ? '#fbbf24'
                                    : ORANGE
                                }`,
                                color: alert.type === 'danger'
                                    ? 'var(--pc-alert-danger-text)'
                                    : alert.type === 'warning'
                                      ? 'var(--pc-alert-warning-text)'
                                      : ORANGE,
                            }}
                        >
                            {alert.message}
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            disabled={newCollection.isSubmit}
                            style={{
                                width: '100%', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 10,
                                padding: '16px 24px', borderRadius: 12,
                                background: newCollection.isSubmit ? ORANGE_DARK : ORANGE,
                                border: 'none', color: '#fff',
                                fontWeight: 600, fontSize: 19,
                                cursor: newCollection.isSubmit ? 'not-allowed' : 'pointer',
                                opacity: newCollection.isSubmit ? 0.7 : 1,
                                transition: 'background .15s, opacity .15s',
                            }}
                            onMouseEnter={e => { if (!newCollection.isSubmit) (e.currentTarget as HTMLElement).style.background = ORANGE_DARK; }}
                            onMouseLeave={e => { if (!newCollection.isSubmit) (e.currentTarget as HTMLElement).style.background = ORANGE; }}
                        >
                            {newCollection.isSubmit ? (
                                <>
                                    <svg style={{ animation: 'spin 1s linear infinite', width: 16, height: 16 }} viewBox="0 0 24 24" fill="none" aria-hidden>
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeOpacity="0.25" />
                                        <path fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                    </svg>
                                    {t('words.loading')}
                                </>
                            ) : (
                                <>
                                    {t('form.dataCollect.start')}
                                    <svg
                                        width={24}
                                        height={24}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden={true}
                                        style={{ flexShrink: 0, marginLeft: 2 }}
                                    >
                                        <path
                                            d="M5 12h14m0 0-5-5m5 5-5 5"
                                            stroke="currentColor"
                                            strokeWidth={2.4}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </>
                            )}
                        </button>
                    </div>
                </form>

            </div>
        </div>
    )
}

export default DataCollectCreate
